import { t } from 'ttag'

export const CLEAR_VIDEO_CACHE = t`Clear Video Cache!!!`
export const CLEAR_VIDEO_CACHE_INSTRUCTIONS_1 = t`!!! First close ALL other tabs, windows, or "App" for SLTT (sltt-bible.net) then`
export const CLEAR_VIDEO_CACHE_INSTRUCTIONS_2 = t`!!! You MUST close and reopen SLTT or refresh page to make this take effect`

export const HOURS_TO_DISABLE_IMPLICIT_DOWNLOADS = 8

/** get localStorage.dbSaveErrorDetectedAt as Date() */
export const getDbSaveErrorDetectedAt = () => {
    const dbSaveErrorDetectedAt = localStorage.dbSaveErrorDetectedAt
    return dbSaveErrorDetectedAt ?
        new Date(dbSaveErrorDetectedAt) : null
}

export const didHappenedWithinHourWindow = (dtEvent: Date | null, maxPastHours: number) => {
    if (!dtEvent) return false
    const maxHoursAgoDate = new Date(Date.now() - maxPastHours * 60 * 60 * 1000)
    // (e.g. 1 hour ago is greater than 8 hours ago)
    return dtEvent > maxHoursAgoDate
}

/** get the storage usage at the time of a db save error crash */
export const getStorageUsageAtCrash = () => {
    const maxUsage = localStorage.storageUsageAtCrash
    if (maxUsage) {
        const lastMaxUsageBytes = parseInt(maxUsage, 10)
        if (!isNaN(lastMaxUsageBytes)) {
            return lastMaxUsageBytes
        }
    }
    return 0
}

export const toMb = (bytes: number, fractionDigits = 0) => {
    return (bytes / (1024 * 1024)).toFixed(fractionDigits)
}

export const HOURS_TO_KEEP_PORTION_DOWNLOADS = 1

/** update localStorage with current date and time was in the process of downloading portion data
 * This will be used to allow the app to try to surpass it's hard quota as long as it doesn't cause
 * a db save error
 */
export const updatePortionDownloadedAt = () => {
    // Update the localStorage with the current date and time
    localStorage.portionDownloadedAt = new Date().toISOString()
}

export const getPortionDownloadedAt = () => {
    const portionDownloadedAt = localStorage.portionDownloadedAt
    if (portionDownloadedAt) {
        return new Date(portionDownloadedAt)
    }
    // forever ago
    return new Date(0)
}

/**
 * determines if user did portion download in the past hour and if 
 * there was no db save error in the past 8 hours
 * in this mode, SLTT tries to protect the videos that were downloaded in the past hour
 * from being deleted by makeSpaceAvailable()
 * 
 * However, if the user for whatever reason wants to do Settings > Debug > Clear Video Cache!!
 * then don't apply this mode in that context
 * @returns {boolean} - true if the app is in portion download mode, false otherwise.
 */
export const getInPortionDownloadMode = () => {
    return didHappenedWithinHourWindow(getPortionDownloadedAt(), HOURS_TO_KEEP_PORTION_DOWNLOADS) &&
            !didHappenedWithinHourWindow(getDbSaveErrorDetectedAt(), HOURS_TO_DISABLE_IMPLICIT_DOWNLOADS)
}

/**
 * 1. if localStorage.disableImplicit is set to true then disableImplicit
 * 2. if browser storage quota is less than 2GB then disableImplicit
 * 3. if should free space and safeToDeletes is less than what's needed to free space
 * 4. has encountered save to database error in past 4 hours
 * @returns {boolean} - true if implicit downloads are disabled.
 */
export const computeShouldDisableImplicitDownloads = async ({
    getSafeToDeleteVcrs,
    dbSaveErrorDetectedAt,
    warnOnceDisableImplicitLocalStorage,
    warnOnceDisableImplicitQuota,
    warnOnceDisableImplicitSafeVcrsLessthanBytesToFree,
    warnOnceDisableImplicitEncounteredSaveToDBError,
}: {
    getSafeToDeleteVcrs: () => Promise<{
        bytesToFree: number,
        vcrsSafeToDeleteBlobsSize: number,
    }>,
    dbSaveErrorDetectedAt: Date | null,
    warnOnceDisableImplicitLocalStorage: (msg: string) => void,
    warnOnceDisableImplicitQuota: (msg: string) => void,
    warnOnceDisableImplicitSafeVcrsLessthanBytesToFree: (msg: string) => void,
    warnOnceDisableImplicitEncounteredSaveToDBError: (msg: string) => void,
}) => {
    try {
        // 1. if localStorage.disableImplicit is set to true then disableImplicit
        if (localStorage.getItem('disableImplicit') !== null) {
            const disableImplicit = localStorage.getItem('disableImplicit') === 'true'
            if (disableImplicit) {
                warnOnceDisableImplicitLocalStorage('implicit downloads are disabled by localStorage')
            } else {
                warnOnceDisableImplicitLocalStorage('implicit downloads are enabled by localStorage')
            }
            return !!disableImplicit
        }
        // NOTE: Given that persistent() does not seem to work unless the user adds the site as a bookmark or
        // to their home screen, and given that if the user does those things the browser will grant
        // them more storage, persistent() does not seem to add anything.
        // // 2. if browser is not persistent() storage, then disableImplicit
        // if (!(await navigator.storage.persisted())) {
        //     // Browsers (e.g. Chrome, Safari) can have a low (200-300mb) "best effort" or "soft limit" quota
        //     // if the browser has not been able to determine whether the site has "importance" to the user
        //     // this can happen for a freshly installed browsers or cleared site data/cookies
        //     // possible ways around this: 
        //     // 1) for SLTT to request persistent storage via await navigator.storage.persist()
        //     // 2) for the user to install the website as a PWA "App" to their desktop
        //     // 3) Adding the site to the browser bookmarks or home screen
        //     // 4) for the user to use the site enough to make the browser consider it important
        //     VideoCache.disableImplicit = true
        //     warnOnceDisableImplicitNotPersistentStorage('implicit downloads are disabled by non-persistent storage')
        //     return !!VideoCache.disableImplicit
        // }

        // 2. if browser storage quota is less than 2GB then disableImplicit
        const { quota } = await navigator?.storage?.estimate?.() /* not a function on iOS Mobile/Safari */
            ?? { quota: undefined, usage: undefined }
        if (quota === undefined || quota < 2 * 1024 * 1024 * 1024) {
            warnOnceDisableImplicitQuota(`implicit downloads are disabled by browser storage quota (${quota && (quota / (1024 * 1024)).toFixed(1)}mb) < 2GB`)
            return true
        }
        // 3. if bytesToFree and safeToDeletes is less than what's needed to free space
        const { bytesToFree, vcrsSafeToDeleteBlobsSize } = await getSafeToDeleteVcrs()
        if (bytesToFree > 0 && vcrsSafeToDeleteBlobsSize < bytesToFree) {
            const msg = `implicit downloads are disabled by low free space (${(vcrsSafeToDeleteBlobsSize / (1024 * 1024)).toFixed(1)
                }mb) < ${(bytesToFree / (1024 * 1024)).toFixed(1)}mb`
            warnOnceDisableImplicitSafeVcrsLessthanBytesToFree(msg)
            return true
        }
        // 4. has encountered save to database error in past 8 hours
        // TODO:
        if (dbSaveErrorDetectedAt && didHappenedWithinHourWindow(dbSaveErrorDetectedAt, HOURS_TO_DISABLE_IMPLICIT_DOWNLOADS)) {
            warnOnceDisableImplicitEncounteredSaveToDBError(
                `implicit downloads are disabled for ${HOURS_TO_DISABLE_IMPLICIT_DOWNLOADS
                } hours after recent database save error at ${dbSaveErrorDetectedAt.toISOString()
                }`)
            return true
        }
        return false
    } finally {
    }
}